import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { attendanceApi } from "@/lib/api/attendance";
import { leaveBalancesApi } from "@/lib/api/leaveBalances";
import { leavesApi } from "@/lib/api/leaves";
import { holidaysApi } from "@/lib/api/holidays";
import { payrollApi } from "@/lib/api/payroll";
import { salaryAdvanceApi } from "@/lib/api/salaryAdvance";
import { expenseApi } from "@/lib/api/expense";
import { helpdeskApi } from "@/lib/api/helpdesk";
import { assetsApi } from "@/lib/api/assets";
import { performanceApi } from "@/lib/api/performance";
import { notificationsApi } from "@/lib/api/notifications";
import { announcementsApi } from "@/lib/api/announcements";
import { employeeRequestsApi } from "@/lib/api/employeeRequests";
import { qk } from "@/lib/query-client";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { StatusBadge } from "@/components/ui/Badge";
import { Icon } from "@/components/layout/Icon";
import { formatDate, formatDateTime, formatMoney } from "@/lib/utils/format";
import { businessToday, formatPunchTime, formatWorkedMinutes } from "@/lib/utils/attendance-date";
import { describeHolidayDay } from "@/lib/attendanceDisplay";
import { can, useAuthStore } from "@/stores/auth-store";
import { useFeatureEnabled } from "@/lib/features";
import { SelfCheckInCard } from "@/features/attendance/SelfCheckInCard";
import { REQUEST_TYPE_LABEL } from "./requestTypes";
import { MyEarningsCard } from "./MyEarningsCard";
import { PaydayCard } from "./PaydayCard";
import { WalletCard } from "./WalletCard";
import { AchievementsCard } from "./AchievementsCard";
import { RecognitionCard } from "./RecognitionCard";
import { QuickActions, TODAY_CARD_ID } from "./QuickActions";

/** ESS Dashboard - the employee's personal HR command centre: where am I
 * today, what needs my attention, what's coming up, what happened recently.
 * Deliberately reads exclusively from existing self-service endpoints
 * (attendance/leave/payroll/salary-advance/helpdesk/assets/performance/
 * notifications/employee-requests) rather than a new aggregate backend
 * route - same "don't duplicate business logic" principle the Phase 1
 * notification/audit work already followed. Each section fails gracefully
 * (its own card just doesn't render) if the caller's tenant has that module
 * disabled, rather than the whole page erroring. */
export function EssDashboardPage() {
  const navigate = useNavigate();
  const employeeId = useAuthStore((s) => s.user?.employee_id);
  const fullName = useAuthStore((s) => s.user?.full_name);

  const attendanceEnabled = useFeatureEnabled("attendance_management");
  const leaveEnabled = useFeatureEnabled("leave_management");
  const leaveBalancesEnabled = useFeatureEnabled("leave_balance_management");
  const requestsEnabled = useFeatureEnabled("employee_requests");
  const payrollEnabled = useFeatureEnabled("payroll_management");
  const salaryAdvanceEnabled = useFeatureEnabled("salary_advance_request");
  const helpdeskEnabled = useFeatureEnabled("hr_helpdesk");
  const assetsEnabled = useFeatureEnabled("asset_management");
  const performanceEnabled = useFeatureEnabled("performance_management");
  const expenseEnabled = useFeatureEnabled("expense_management");
  const announcementsEnabled = useFeatureEnabled("announcement_module");

  // Ask for TODAY explicitly rather than letting the endpoint fall back to
  // its default range. The default is the current month resolved from the
  // tenant's timezone server-side, and `today` below is resolved from the
  // browser's - on the first and last day of a month those two can name
  // different months, and the row this card needs would simply not be in the
  // response. Sending the date the card is going to look for removes the
  // question entirely.
  const todayDate = businessToday();
  const { data: attendance } = useQuery({
    queryKey: qk.attendance.employee(employeeId ?? "", todayDate, todayDate),
    queryFn: () => attendanceApi.forEmployee(employeeId!, todayDate, todayDate),
    enabled: Boolean(employeeId) && attendanceEnabled,
  });
  const today = attendance?.records.find((r) => r.date === todayDate);
  const holidayToday = describeHolidayDay(today);

  const { data: balances } = useQuery({
    queryKey: qk.leaveBalances.self,
    queryFn: leaveBalancesApi.listSelf,
    enabled: leaveBalancesEnabled,
  });
  const totalAvailableLeave = (balances ?? []).reduce(
    (sum, b) => sum + b.opening_balance + b.accrued + b.carried_forward + b.adjustments - b.used - b.pending - b.encashed,
    0,
  );

  // Leave requests I've submitted that are still awaiting a decision - the
  // /leaves list is already self-scoped server-side for a plain Employee
  // (leave.read carries no team/tenant visibility for this role), so no
  // status filter param is needed here beyond client-side narrowing to the
  // two pending stages.
  const { data: myLeaves } = useQuery({
    queryKey: qk.leaves.list({ per_page: 20, mine: true }),
    queryFn: () => leavesApi.list({ per_page: 20 }),
    enabled: leaveEnabled,
  });
  const pendingLeaveRequests = (myLeaves?.items ?? []).filter(
    (l) => l.status === "pending_manager" || l.status === "pending_hr",
  );

  const { data: pendingExpenses } = useQuery({
    queryKey: qk.expense.my({ status: "pending_approval", per_page: 5, mine: true }),
    queryFn: () => expenseApi.my({ status: "pending_approval", per_page: 5 }),
    enabled: expenseEnabled,
  });

  const { data: announcements } = useQuery({
    queryKey: qk.announcements.list({ per_page: 4 }),
    queryFn: () => announcementsApi.list({ per_page: 4 }),
    enabled: announcementsEnabled,
  });

  const { data: holidaysPage } = useQuery({
    queryKey: qk.holidays.list(new Date().getFullYear(), { per_page: 100 }),
    queryFn: () => holidaysApi.list(new Date().getFullYear(), { per_page: 100 }),
  });
  const upcomingHolidays = (holidaysPage?.items ?? [])
    .filter((h) => h.date >= businessToday())
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 4);

  const { data: payslips } = useQuery({
    queryKey: qk.payroll.myPayslips,
    queryFn: payrollApi.myPayslips,
    enabled: payrollEnabled,
  });
  const latestPayslip = (payslips ?? [])[0];

  const { data: advances } = useQuery({
    queryKey: qk.salaryAdvance.list({ per_page: 3 }),
    queryFn: () => salaryAdvanceApi.list({ per_page: 3 }),
    enabled: salaryAdvanceEnabled,
  });
  const openAdvance = (advances?.items ?? []).find((a) => a.status !== "completed" && a.status !== "rejected");

  const { data: ticketSummary } = useQuery({
    queryKey: qk.helpdesk.summary({ scope: "own" }),
    queryFn: () => helpdeskApi.summary({ scope: "own" }),
    enabled: helpdeskEnabled,
  });
  const openTickets = (ticketSummary?.open ?? 0) + (ticketSummary?.in_progress ?? 0) + (ticketSummary?.assigned ?? 0);

  const { data: myAssets } = useQuery({
    queryKey: qk.assets.my,
    queryFn: assetsApi.myAssets,
    enabled: assetsEnabled,
  });

  const { data: performance } = useQuery({
    queryKey: qk.performance.dashboard,
    queryFn: performanceApi.dashboard,
    enabled: performanceEnabled && can("performance.dashboard.view"),
  });

  const { data: pendingRequests } = useQuery({
    queryKey: qk.employeeRequests.list({ status: "submitted", scope: "mine", per_page: 5 }),
    queryFn: () => employeeRequestsApi.list({ status: "submitted", per_page: 5 }),
    enabled: requestsEnabled,
  });

  const { data: recentActivity } = useQuery({
    queryKey: ["notifications", "recent"],
    queryFn: () => notificationsApi.list({ per_page: 6 }),
  });

  return (
    <div>
      <PageHeader title={fullName ? `Welcome back, ${fullName.split(" ")[0]}` : "My Dashboard"} description="Your personal HR overview." />

      <div className="mb-6">
        <QuickActions
          checkIn={attendanceEnabled && can("attendance.self_mark")}
          attendance={attendanceEnabled && (can("attendance.self_read") || can("attendance.read"))}
          payslip={payrollEnabled && (can("payslip.self_read") || can("payslip.read"))}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {attendanceEnabled && (
            <Card id={TODAY_CARD_ID} className="scroll-mt-20">
              <CardHeader>
                <CardTitle>Today</CardTitle>
                {holidayToday ? (
                  <span className="rounded-full bg-teal-50 px-2.5 py-0.5 text-xs font-medium text-teal-700">{holidayToday.label}</span>
                ) : (
                  today && <StatusBadge status={today.status} />
                )}
              </CardHeader>
              <CardBody className="space-y-4">
                {/* Check-in is not compulsory on a holiday - no fake
                    check-in is ever created, so first_in/last_out/worked
                    time are genuinely absent/zero until the employee
                    actually chooses to work it (see SelfCheckInCard's own
                    "Work on Holiday" action). */}
                <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
                  <div>
                    <p className="text-slate-500">Check-in</p>
                    <p className="mt-0.5 font-medium text-slate-800">{formatPunchTime(today?.first_in)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Check-out</p>
                    <p className="mt-0.5 font-medium text-slate-800">{formatPunchTime(today?.last_out)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Working time</p>
                    <p className="mt-0.5 font-medium text-slate-800">{formatWorkedMinutes(today?.worked_minutes)}</p>
                  </div>
                </div>
                {can("attendance.self_mark") && <SelfCheckInCard todayAttendance={today} />}
              </CardBody>
            </Card>
          )}

          {payrollEnabled && (can("payslip.self_read") || can("payslip.read")) && employeeId && <MyEarningsCard />}

          {attendanceEnabled && (can("attendance.self_read") || can("attendance.read")) && employeeId && <AchievementsCard />}

          <Card>
            <CardHeader>
              <CardTitle>My HR</CardTitle>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {leaveBalancesEnabled && <HrTile icon="calendar" label="Leave balance" value={totalAvailableLeave.toFixed(1)} onClick={() => navigate("/leaves")} />}
                {leaveEnabled && (
                  <HrTile icon="calendar-check" label="Pending leave" value={String(pendingLeaveRequests.length)} onClick={() => navigate("/leaves")} />
                )}
                {expenseEnabled && (
                  <HrTile icon="receipt" label="Pending expenses" value={String(pendingExpenses?.meta?.total ?? 0)} onClick={() => navigate("/expenses/my")} />
                )}
                {payrollEnabled && (
                  <HrTile
                    icon="wallet"
                    label="Latest payslip"
                    value={latestPayslip ? formatMoney(latestPayslip.net_pay) : "—"}
                    onClick={() => navigate("/my-payslips")}
                  />
                )}
                {salaryAdvanceEnabled && (
                  <HrTile
                    icon="credit-card"
                    label="Salary advance"
                    value={openAdvance ? openAdvance.status.replace(/_/g, " ") : "None active"}
                    onClick={() => navigate("/salary-advances/my")}
                  />
                )}
                {helpdeskEnabled && <HrTile icon="help-circle" label="Open tickets" value={String(openTickets)} onClick={() => navigate("/helpdesk/my")} />}
                {assetsEnabled && <HrTile icon="layers" label="My assets" value={String((myAssets ?? []).length)} onClick={() => navigate("/my-assets")} />}
                {performanceEnabled && performance && (
                  <HrTile
                    icon="target"
                    label="My goal progress"
                    value={performance.my_goal_progress != null ? `${Math.round(performance.my_goal_progress)}%` : `${(performance.my_goals ?? []).length} goals`}
                    onClick={() => navigate("/performance/my")}
                  />
                )}
                {requestsEnabled && (
                  <HrTile
                    icon="clipboard-list"
                    label="Pending requests"
                    value={String(pendingRequests?.meta?.total ?? 0)}
                    onClick={() => navigate("/ess/request-center")}
                  />
                )}
              </div>
            </CardBody>
          </Card>

          {requestsEnabled && (pendingRequests?.items.length ?? 0) > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Pending my requests</CardTitle>
              </CardHeader>
              <CardBody>
                <ul className="divide-y divide-slate-100">
                  {pendingRequests!.items.map((r) => (
                    <li key={r.id} className="flex items-center justify-between gap-3 py-2">
                      <button onClick={() => navigate(`/ess/request-center/${r.id}`)} className="text-left text-sm text-primary-700 hover:underline">
                        {r.title}
                      </button>
                      <span className="text-xs text-slate-400">{REQUEST_TYPE_LABEL[r.type] ?? r.type}</span>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          {payrollEnabled && (can("payslip.self_read") || can("payslip.read")) && employeeId && <WalletCard />}

          {payrollEnabled && (can("payslip.self_read") || can("payslip.read")) && employeeId && <PaydayCard />}

          {employeeId && <RecognitionCard />}

          {announcementsEnabled && (
            <Card>
              <CardHeader>
                <CardTitle>Announcements</CardTitle>
                <button onClick={() => navigate("/announcements")} className="text-xs font-medium text-primary-700 hover:underline">
                  View all →
                </button>
              </CardHeader>
              <CardBody>
                {(announcements?.items.length ?? 0) === 0 ? (
                  <p className="py-4 text-center text-sm text-slate-400">No announcements yet</p>
                ) : (
                  <ul className="space-y-3">
                    {announcements!.items.map((a) => (
                      <li key={a.id} className="flex items-start gap-2">
                        {!a.is_read && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-600" />}
                        <div className={a.is_read ? "pl-3.5" : undefined}>
                          <p className="text-sm font-medium text-slate-800">{a.title}</p>
                          <p className="text-xs text-slate-400">{formatDateTime(a.published_at)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Upcoming Holidays</CardTitle>
            </CardHeader>
            <CardBody>
              {upcomingHolidays.length === 0 ? (
                <p className="py-4 text-center text-sm text-slate-400">No upcoming holidays</p>
              ) : (
                <ul className="space-y-2">
                  {upcomingHolidays.map((h) => (
                    <li key={h.id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">{h.name}</span>
                      <span className="text-slate-400">{formatDate(h.date)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardBody>
              {(recentActivity?.items.length ?? 0) === 0 ? (
                <p className="py-4 text-center text-sm text-slate-400">Nothing yet</p>
              ) : (
                <ul className="space-y-3">
                  {recentActivity!.items.map((n) => (
                    <li key={n.id}>
                      <p className="text-sm text-slate-800">{n.title}</p>
                      <p className="text-xs text-slate-400">{formatDateTime(n.created_at)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}

function HrTile({ icon, label, value, onClick }: { icon: string; label: string; value: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="rounded-lg border border-slate-200 p-3 text-left transition hover:border-primary-300 hover:bg-primary-50/40">
      <Icon name={icon} className="h-4 w-4 text-slate-400" />
      <p className="mt-2 truncate text-sm font-semibold text-slate-800 capitalize">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </button>
  );
}
